import { useFormikContext } from 'formik';
import React, { Fragment, useState } from 'react';
import { Collapse } from 'react-collapse';
import { useSelector, useDispatch } from 'react-redux';
import { find, get } from 'lodash';
import { t } from 'Src/i18n';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { Button } from 'Components/frameworks/button/Button';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { Toast } from 'Components/frameworks/toast/Toast';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import {
  travelCompanionMapper,
  setTravelCompanionBlockFormErrors,
  getTravelCompanionInfos,
} from 'Components/widgets/contact-info/ContactInfoUtils';
import { useAppContext } from 'Context/ReactAppContext';
import {
  addTravelCompanion,
  selectTravelCompanion,
} from 'Store/booking/travel-companion/travel-companion.action';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { TravelCompanionBlock } from './TravelCompanionBlock';
type Props = {
  parentIndex: number;
  isUserConnected: boolean;
  isMain: boolean;
  isChild: boolean;
  personalInfoFormContent: React.ReactElement<React.ComponentProps<any>, any>;
  attendeeIdentifier: string;
  setIsDisabled: (InputState: boolean) => void;
};
export const PersonInfoForm = ({
  parentIndex,
  isUserConnected,
  isMain,
  isChild,
  personalInfoFormContent,
  attendeeIdentifier,
  setIsDisabled,
}: Props) => {
  const { errors, values, setFieldValue, setTouched, setFieldTouched } = useFormikContext();
  const [showCompanionForm, setShowCompanionForm] = useState('');
  const [isAdded, setIsAdded] = useState(false);
  const isValid = !get(errors, attendeeIdentifier, false);
  const appContext = useAppContext();
  const { trackClickEvent } = useTrackEvent();
  const travelCompanionSelector = useSelector((state) => state.booking.travelCompanion);
  const { travelCompanionList, hasParentIndexInSelectedIndexes, isDisabled } =
    getTravelCompanionInfos({
      travelCompanionSelector,
      isChild,
      parentIndex,
    });
  const travelCompanionIndex = isChild ? parentIndex - (values?.adults?.length || 0) : parentIndex;
  const formList = isChild ? values.children : values.adults;
  const valuesList = isChild ? values?.children : values?.adults?.slice(1);
  const selectedCompanions = travelCompanionList.filter(({ isSelected }) => isSelected);
  const selectedNumber = selectedCompanions.length;
  const notSelectedNumber = travelCompanionList.filter(({ isSelected }) => !isSelected).length;
  const showRegularForm = // In case we have the number of attendees greater than the travel companions
    // then we show the rest of those attendees to the old regular form block
    notSelectedNumber === 0 &&
    selectedNumber < valuesList?.length &&
    !hasParentIndexInSelectedIndexes;
  const shouldDisplayTravelCompanion =
    isUserConnected && isFeatureFlipped(appContext)('booking.travelCompanions') && !showRegularForm;
  const dispatch = useDispatch();
  const isCompanionAddForm = showCompanionForm === 'ADD';
  const shouldDisplayAddCompanionButton = showCompanionForm !== 'EDIT';

  const handleAddTravelCompanion = () => {
    if (isValid) {
      const newCompanionId = travelCompanionSelector.list.length;
      const newTravelCompanion = {
        ...formList.map(travelCompanionMapper)[travelCompanionIndex],
        companionId: newCompanionId,
        isSelected: true,
        isChild,
      };
      dispatch(addTravelCompanion(newTravelCompanion));
      setIsAdded(true);
      setShowCompanionForm('');
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
        detail_click: 'validate_new_companion',
      });
      // Select it immediatly after Adding it
      dispatch(
        selectTravelCompanion({
          companionId: newCompanionId,
          parentIndex,
          position: travelCompanionList.length,
        }),
      );
      scrollToElement(`#attendee-${parentIndex}`, 500);
    } else {
      setTravelCompanionBlockFormErrors({
        errors,
        isChild,
        travelCompanionIndex,
        setFieldTouched,
      });
    }
  };

  const clearAttendeeData = () => {
    setTouched({});
    setFieldValue(
      attendeeIdentifier,
      isChild
        ? {}
        : {
            civility: 'MR',
          },
    );
  };

  if (isMain || !shouldDisplayTravelCompanion) {
    return personalInfoFormContent;
  }

  return (
    <Fragment>
      {isAdded && (
        <Toast
          color={COLORS.GREEN}
          icon={ICONS.CHECK_OUTLINE}
          label={t('booking.participants.travelCompanion.adultAdded')}
          onClose={() => setIsAdded(false)}
          visible={isAdded}
        />
      )}

      <div className="max-w-[90vw] p-20">
        <div className="mb-10">
          <p className="mb-20 text-14 font-bold">
            {!isChild
              ? t('booking.participants.travelCompanion.selectAdult')
              : t('booking.participants.travelCompanion.selectChildren')}
          </p>
          <Scrollable className="-mx-20 flex border-b-1 border-grey" navPosition="center">
            <div className="flex flex-row p-10 px-20">
              {travelCompanionList.map((attendee, index) => {
                return (
                  <TravelCompanionBlock
                    travelCompanion={attendee}
                    isChild={isChild}
                    key={`${(attendee?.id || '').toString()}+${attendee.firstName}+${attendee.lastName}`}
                    position={index}
                    parentIndex={parentIndex}
                    setIsDisabled={setIsDisabled}
                    isDisabled={isDisabled}
                    setShowCompanionForm={setShowCompanionForm}
                    includedInSelectedIndexes={
                      !!find(travelCompanionSelector.selectedIndexes, {
                        index: parentIndex,
                        position: index,
                      })
                    }
                    attendeeIdentifier={attendeeIdentifier}
                    clearAttendeeData={clearAttendeeData}
                  />
                );
              })}
            </div>
          </Scrollable>
        </div>

        {shouldDisplayAddCompanionButton && (
          <div className="flex justify-end pt-10">
            <Button
              iconLeft={{
                name: ICONS.PEOPLE,
              }}
              iconRight={{
                name: ICONS.ARROW_RIGHT,
                rotation: isCompanionAddForm ? 90 : 0,
                width: '0.875rem',
              }}
              isDisabled={isDisabled}
              onClick={() => {
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_PARTICIPANTS, {
                  detail_click: 'add_new_companion',
                });
                setIsDisabled(false);
                setFieldValue(
                  attendeeIdentifier,
                  isChild
                    ? {}
                    : {
                        civility: 'MR',
                        document: {
                          passport: {
                            type: 'PASSPORT',
                          },
                          idcard: {
                            type: 'IDCARD',
                          },
                        },
                      },
                );
                setShowCompanionForm(!showCompanionForm ? 'ADD' : '');
              }}
              size="small"
            >
              {t('booking.participants.travelCompanion.newCompanion')}
            </Button>
          </div>
        )}
        <Collapse isOpened={showCompanionForm}>
          <div className="-mx-20 mt-20">{personalInfoFormContent}</div>
          <div className="flex justify-end">
            {isCompanionAddForm && (
              <Button className="mt-10" onClick={handleAddTravelCompanion} isDisabled={false}>
                {t('booking.participants.travelCompanion.addNewCompanion')}
              </Button>
            )}
          </div>
        </Collapse>
      </div>
    </Fragment>
  );
};
